// lib/list/listAutoMentions.ts
// Auto mode: a short agent step that turns a plain List request ("edit the last one", "the woman
// on the bench") into the explicit @ mentions the List pipeline requires. The pipeline and its
// checks are unchanged; this only writes the mentions a Manual user would have typed.
import { z } from 'zod';

export type ListAutoCandidate = {
  /** The exact token without '@', e.g. output:abc, last, object:card, a persona handle. */
  handle: string;
  kind: 'output' | 'persona' | 'object' | 'archetype' | 'folder';
  label: string;
  /** Outputs only: 1 is the newest completed image. */
  recency?: number;
  /** Outputs only: open in the viewer right now. */
  focused?: boolean;
};

export const listAutoRequestSchema = z.object({
  message: z.string().trim().min(1).max(4000),
  candidates: z.array(z.object({
    handle: z.string().trim().min(1).max(200),
    kind: z.enum(['output', 'persona', 'object', 'archetype', 'folder']),
    label: z.string().max(300),
    recency: z.number().int().min(1).max(10_000).optional(),
    focused: z.boolean().optional(),
  }).strict()).max(300),
  conversation: z.array(z.object({ role: z.enum(['user', 'assistant']), text: z.string().max(1200) }).strict()).max(12).optional(),
}).strict();

export const listAutoAnswerSchema = z.object({
  message: z.string().max(6000),
  editTarget: z.string().max(200).nullable(),
});
export type ListAutoAnswer = z.infer<typeof listAutoAnswerSchema>;

export const LIST_AUTO_SYSTEM_PROMPT = `You prepare a user's message for a marketing image tool. The tool acts only on items named with an exact @handle.
Return the user's message with the @handles of the items it refers to inserted, and nothing else changed: same language, same wording, same instructions. Replace a phrase that designates an item ("the last image", "this one", "the photo with the woman", "Lisa", "the Westfield card") by its @handle, or add the @handle right after it.
Outputs are the user's generated images: recency 1 is the newest, "focused" is the one open on screen ("this image" usually means it, otherwise the newest). Use a caption only when it clearly matches; when several outputs match the description, it means the most recent of them (lowest recency).
editTarget is the output handle the user wants changed (edit, retouch, reframe, vary, upscale, "make it ..."), or null when they want something new. At most one.
Use the recent conversation to resolve what the message points back to: "it", "the same", "again", "warmer", "the other one", "the previous version", "do the same for Lisa". A follow-up about a result usually means the output that result produced, and @output handles already written in earlier user messages name the images discussed.
If the message designates nothing, or you are not sure which item it means, return it unchanged with editTarget null: the tool will ask the user. Never invent a handle, never add items the user did not refer to, never answer the user.`;

const TOKEN = /(^|[\s(])@([a-z0-9](?:[a-z0-9:_.-]*[a-z0-9])?)/gi;

/**
 * Trust boundary: the model's rewrite may only add handles the client offered, and must keep the
 * user's words. Anything else falls back to the message as typed (Manual behaviour).
 */
export function applyListAutoAnswer(original: string, answer: ListAutoAnswer, candidates: ListAutoCandidate[]): string {
  const offered = new Map(candidates.map(candidate => [candidate.handle.toLowerCase(), candidate]));
  const typed = new Set([...original.matchAll(TOKEN)].map(match => match[2]!.toLowerCase()));
  const rewritten = answer.message.trim();
  if (!rewritten) return original;
  for (const match of rewritten.matchAll(TOKEN)) {
    const handle = match[2]!.toLowerCase();
    if (!offered.has(handle) && !typed.has(handle)) return original;
  }
  // A mention pass may replace a designating phrase by a handle, nothing more: a word the user did
  // not write means the model answered or rephrased, and losing most of the words means it dropped
  // the request.
  const words = (text: string) => text.replace(TOKEN, '$1').toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  const originalWords = new Set(words(original));
  const rewrittenWords = words(rewritten);
  if (rewrittenWords.some(word => !originalWords.has(word))) return original;
  if (new Set(rewrittenWords).size < Math.ceil(originalWords.size / 3)) return original;

  const target = answer.editTarget?.replace(/^@/, '').toLowerCase();
  const edit = target ? offered.get(target) : undefined;
  if (!edit || edit.kind !== 'output' || edit.handle === 'last') return rewritten;
  // The edit target is marked the way the composer marks a chosen @last, which the server reads as
  // the explicit edit source. The number is the output's own position, not a claim it is the newest.
  const number = /^Output (\d+)/.exec(edit.label)?.[1];
  if (!number) return rewritten;
  let marked = false;
  const result = rewritten.replace(TOKEN, (token, prefix: string, handle: string) => {
    if (marked || handle.toLowerCase() !== edit.handle.toLowerCase()) return token;
    marked = true;
    return `${prefix}@${edit.handle}[last:${number}]`;
  });
  return marked ? result : rewritten;
}

/** Auto only steps in when the user named nothing and asked for no command. */
export const needsListAutoMentions = (message: string) => {
  const trimmed = message.trim();
  return trimmed.length > 0 && !trimmed.startsWith('/') && ![...trimmed.matchAll(TOKEN)].length;
};

/** What a stored user message asked the agent: the Auto version with mentions when there is one. */
export const storedUserPrompt = (payload: unknown): string | undefined => {
  const data = payload as { technicalPrompt?: unknown; content?: unknown } | null;
  return typeof data?.technicalPrompt === 'string' ? data.technicalPrompt : typeof data?.content === 'string' ? data.content : undefined;
};
